import axios from "axios";
import { server } from "../server";

const getAuthHeader = () => {
  const token = localStorage.getItem("dbToken");
  return { Authorization: `Bearer ${token}` };
};

export const createFolderAPI = async (name, parentId = null, isPrivate = false) => {
  try {
    const response = await axios.post(
      `${server}/drive/folder`,
      {
        name,
        parentId: parentId || null,
        isPrivate
      },
      {
        headers: {
          ...getAuthHeader(),
          "Content-Type": "application/json",
          "accept": "*/*",
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getFoldersAPI = async (sortBy = "name", sortOrder = "asc") => {
  try {
    const response = await axios.get(
      `${server}/drive/folders?userWise=false&sharedWithMe=false&parentFoldersOnly=false&sortBy=${sortBy}&sortOrder=${sortOrder}`,
      {
        headers: getAuthHeader(),
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};
export const getPrivateContentAPI = async (sortBy = "name", sortOrder = "asc") => {
  try {
    const response = await axios.get(
      `${server}/drive/folders?userWise=false&sharedWithMe=false&parentFoldersOnly=false&isPrivate=true&deleted=false&sortBy=${sortBy}&sortOrder=${sortOrder}`,
      {
        headers: getAuthHeader(),
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getFolderContentAPI = async (id, sortBy = "name", sortOrder = "asc", isPrivate = false) => {
  try {
    const response = await axios.get(`${server}/drive/folder/${id}?sortBy=${sortBy}&sortOrder=${sortOrder}&isPrivate=${isPrivate}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const uploadFileAPI = async (file, folderId = null, onUploadProgress, signal, isPrivate = false) => {
  try {
    const formData = new FormData();
    formData.append("File", file);
    formData.append("DisplayName", file.name);
    formData.append("IsPrivate", isPrivate);
    if (folderId) {
      formData.append("FolderId", folderId);
    }

    const response = await axios.post(`${server}/drive/file`, formData, {
      headers: {
        ...getAuthHeader(),
        "Content-Type": "multipart/form-data",
        "accept": "*/*",
      },
      onUploadProgress,
      signal,
    });
    return response.data;
  } catch (error) {
    throw error?.response?.data?.message || error?.message;
  }
};

export const viewFileAPI = (id) => {
  return `${server}/drive/file/${id}/view`;
};

export const requestDownloadAPI = async (id) => {
  try {
    const response = await axios.post(
      `${server}/drive/file/${id}/request-download`,
      {},
      {
        headers: {
          ...getAuthHeader(),
          "accept": "*/*",
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getDownloadUrl = (tempGuid) => {
  return `${server}/drive/file/${tempGuid}/download`;
};

export const renameFileAPI = async (id, newName) => {
  try {
    const response = await axios.patch(
      `${server}/drive/file/${id}/rename`,
      { displayName: newName },
      {
        headers: {
          ...getAuthHeader(),
          "Content-Type": "application/json",
          "accept": "*/*",
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const renameFolderAPI = async (id, newName) => {
  try {
    const response = await axios.patch(
      `${server}/drive/folder/${id}/rename`,
      { name: newName },
      {
        headers: {
          ...getAuthHeader(),
          "Content-Type": "application/json",
          "accept": "*/*",
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getUsersAPI = async () => {
  try {
    const response = await axios.get(`${server}/auth/users`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const grantAccessAPI = async (userIds, resources, permission = "view") => {
  try {
    const response = await axios.post(
      `${server}/drive/access/grant`,
      {
        grantedToUserIds: Array.isArray(userIds) ? userIds : [userIds],
        resources: Array.isArray(resources) ? resources : [resources],
        permission: permission,
      },
      {
        headers: {
          ...getAuthHeader(),
          "Content-Type": "application/json",
          "accept": "*/*",
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};
export const deleteFolderAPI = async (id) => {
  try {
    const response = await axios.delete(`${server}/drive/folder/${id}`, {
      headers: {
        ...getAuthHeader(),
        "accept": "*/*",
      },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteFileAPI = async (id) => {
  try {
    const response = await axios.delete(`${server}/drive/file/${id}`, {
      headers: {
        ...getAuthHeader(),
        "accept": "*/*",
      },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};
export const getSharedWithMeAPI = async (sortBy = "name", sortOrder = "asc") => {
  try {
    const response = await axios.get(`${server}/drive/access/shared-with-me?sortBy=${sortBy}&sortOrder=${sortOrder}`, {
      headers: {
        ...getAuthHeader(),
        "accept": "*/*",
      },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};
export const getActivityAPI = async (params = {}) => {
  try {
    const defaultParams = {
      page: 1,
      pageSize: 20,
      sortBy: "CreatedAt",
      sortDescending: true,
    };
    const combinedParams = { ...defaultParams, ...params };
    const queryParams = new URLSearchParams(combinedParams).toString();

    const response = await axios.get(`${server}/activity/getAll?${queryParams}`, {
      headers: {
        ...getAuthHeader(),
        "accept": "*/*",
      },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};
export const getActivityReportAPI = async (userId, groupBy = "UserAndAction") => {
  try {
    const response = await axios.get(
      `${server}/activity/report?filterUserId=${userId}&groupBy=${groupBy}`,
      {
        headers: {
          ...getAuthHeader(),
          "accept": "*/*",
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};
export const getTrashAPI = async (sortBy = "name", sortOrder = "asc") => {
  try {
    const response = await axios.get(`${server}/drive/trash?sortBy=${sortBy}&sortOrder=${sortOrder}`, {
      headers: {
        ...getAuthHeader(),
        "accept": "*/*",
      },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};
export const permanentDeleteFolderAPI = async (id) => {
  try {
    const response = await axios.delete(`${server}/drive/folder/${id}/permanent`, {
      headers: {
        ...getAuthHeader(),
        "accept": "*/*",
      },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const recoverFolderAPI = async (id) => {
  try {
    const response = await axios.patch(
      `${server}/drive/folder/${id}/recover`,
      {},
      {
        headers: {
          ...getAuthHeader(),
          "accept": "*/*",
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const permanentDeleteFileAPI = async (id) => {
  try {
    const response = await axios.delete(`${server}/drive/file/${id}/permanent`, {
      headers: {
        ...getAuthHeader(),
        "accept": "*/*",
      },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const recoverFileAPI = async (id) => {
  try {
    const response = await axios.patch(
      `${server}/drive/file/${id}/recover`,
      {},
      {
        headers: {
          ...getAuthHeader(),
          "accept": "*/*",
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};
